export type FaqBlock =
  | { type: "p"; text: string }
  | { type: "h"; text: string }
  | { type: "ul"; items: string[] };

export type FaqItem = {
  question: string;
  blocks: FaqBlock[];
};

export type FaqCategory = {
  id: string;
  label: string;
  items: FaqItem[];
};

const P = (text: string): FaqBlock => ({ type: "p", text });
const H = (text: string): FaqBlock => ({ type: "h", text });
const UL = (items: string[]): FaqBlock => ({ type: "ul", items });

export const supportFaqCategories: FaqCategory[] = [
  {
    id: "pricing",
    label: "Pricing / Refund",
    items: [
      {
        question: "Can I get a refund after purchasing a premium plan?",
        blocks: [
          P(
            "We only offer digital products so no refund cancellation or returns are possible after an order has been placed. If you have a continuing subscription then you can cancel anytime and no charges will be deducted after you cancel the subscription/membership. However any charges deducted before cancellation of membership cannot be refunded! Do read Terms and Conditions before any action. For any other questions regarding cancellation or refunds contact Support. Do let us know the issue we are always there to support you. Also check Privacy practices and other policies at chrome store before downloading our extension.",
          ),
        ],
      },
      {
        question: "What is the pricing of Telegram Bulk Sender?",
        blocks: [
          P("Telegram Bulk Sender offers the following plans:"),
          H("India (INR)"),
          UL([
            "Monthly Plan: ₹699",
            "Quarterly Plan (3 months): ₹1,999",
            "Lifetime Plan: ₹4,999",
            "Payment modes supported: UPI and Stripe.",
          ]),
          H("International (USD)"),
          UL([
            "Monthly Plan: $11.99",
            "Quarterly Plan (3 months): $29.99",
            "Lifetime Plan: $80",
            "Payment mode supported: Stripe.",
          ]),
          P(
            "To view complete plan details, features, or if you have any questions, please visit our pricing page: https://tg-hub.com/tg-sender-telegram-bulk-sender/",
          ),
        ],
      },
      {
        question: "What is the pricing of Telegram Video Downloader?",
        blocks: [
          P("Telegram Video Downloader offers the following plans:"),
          H("India (INR)"),
          UL([
            "Monthly Plan: ₹499",
            "Quarterly Plan (3 months): ₹999",
            "Lifetime Plan: ₹2,499",
            "Payment modes supported: UPI and Stripe.",
          ]),
          H("International (USD)"),
          UL([
            "Monthly Plan: $9.99",
            "Quarterly Plan (3 months): $19.99",
            "Lifetime Plan: $60",
            "Payment mode supported: Stripe.",
          ]),
          P(
            "To view complete plan details, features, or if you have any questions, please visit our pricing page: https://tg-hub.com/telegram-video-downloader/",
          ),
        ],
      },
      {
        question: "What is the pricing of Telegram Translator?",
        blocks: [
          P("Telegram Translator offers the following plans:"),
          H("India (INR)"),
          UL([
            "Monthly Plan: ₹499",
            "Lifetime Plan: ₹2,999",
            "Payment modes supported: UPI and Stripe.",
          ]),
          H("International (USD)"),
          UL([
            "Monthly Plan: $4.99",
            "Lifetime Plan: $60",
            "Payment mode supported: Stripe.",
          ]),
          P(
            "To view complete plan details, features, or if you have any questions, please visit our pricing page: https://www.lang-translator.com/translator-for-telegram",
          ),
        ],
      },
      {
        question: "Is there a way to pay through a QR code or UPI?",
        blocks: [
          P(
            "Yes! Please reach out to our support team through WhatsApp to receive a secure QR or UPI payment option.",
          ),
        ],
      },
    ],
  },
  {
    id: "tech",
    label: "Tech Support",
    items: [
      {
        question: "How to fix issues in Telegram Bulk Sender?",
        blocks: [
          P(
            "To help resolve the issue with Telegram Bulk Sender, please follow the steps below:",
          ),
          P(
            "Kindly remove the Telegram Bulk Sender extension and reinstall it. In some cases, cached data can cause unexpected behavior. Extension link: https://chromewebstore.google.com/detail/telegram-bulk-sender/hmbjoobnolmppflmppjgkpblglbmkemo",
          ),
          P(
            "We also recommend disabling or removing any other Telegram Web extensions, as they may interfere with functionality. For best performance, please use only Telegram Hub products. If the issue continues after following these steps, please let us know. Our team will review your request and assist you accordingly.",
          ),
        ],
      },
      {
        question: "How to fix issues in Telegram Video Downloader?",
        blocks: [
          P(
            "To help resolve the issue with Telegram Video Downloader, please follow the steps below:",
          ),
          P(
            "Kindly remove the Telegram Video Downloader extension and reinstall it. In some cases, cached data can cause unexpected behavior. Extension link: https://chromewebstore.google.com/detail/premium-telegram-video-do/fcfmhkgnglllagldahcfpnocepofmmao",
          ),
          P(
            "We also recommend disabling or removing any other Telegram Web extensions, as they may interfere with functionality. For best performance, please use only Telegram Hub products. If the issue continues after following these steps, please let us know. Our team will review your request and assist you accordingly.",
          ),
        ],
      },
      {
        question: "How to fix issues in Telegram Web Translator?",
        blocks: [
          P(
            "To help resolve the issue with Telegram Web Translator, please follow the steps below:",
          ),
          P(
            "Kindly remove the Telegram Web Translator extension and reinstall it. In some cases, cached data can cause unexpected behavior. Extension link: https://chromewebstore.google.com/detail/telegram-web-translator/mcemogfodjmgbhahncfibpgnmbcmiimh",
          ),
          P(
            "We also recommend disabling or removing any other Telegram Web extensions, as they may interfere with functionality. For best performance, please use only Telegram Hub products. If the issue continues after following these steps, please let us know. Our team will review your request and assist you accordingly.",
          ),
        ],
      },
    ],
  },
  {
    id: "how-to",
    label: "How to use",
    items: [
      {
        question: "How to send bulk messages using Telegram Bulk Sender?",
        blocks: [
          P(
            "Telegram Bulk Sender allows you to reach a large audience safely and efficiently. You can get started using the steps below:",
          ),
          UL([
            "Install the extension: Add the extension to Chrome and toggle it to automatically redirect to Telegram Web.",
            "Add contacts: Manually type numbers (with country codes) or upload a bulk list via an Excel/CSV file.",
            "Compose your message: Write your text in the message area; you can also save these as templates for future use.",
            "Attach media: Add up to seven attachments, including images, videos, or documents.",
            "Set a Time Gap: Configure a delay (recommended 15–20 seconds) between messages to ensure safe delivery.",
            'Track performance: After sending, use the "Reports" tab to view delivery success ratios and download data.',
          ]),
          P(
            "You can view the complete step-by-step tutorial here: https://youtu.be/_N7-iFk5cRk?si=M8lca7MqSSgQ6jP0",
          ),
        ],
      },
      {
        question: "How to download videos using Telegram Video Downloader?",
        blocks: [
          P(
            "Telegram Video Downloader lets you save media from chats in just one click. Follow these steps:",
          ),
          UL([
            "Install the extension: Add the tool to your browser and ensure it is pinned to the toolbar.",
            "Scan for videos: Open Telegram Web and click the extension icon to see a list of all available videos on the current page.",
            'Direct Download: Alternatively, look for the green "Download" button that appears directly over images and videos in your chat bar.',
            'Bulk Images: Switch to the "Images" tab in the extension to preview and download all pictures from a chat at once.',
            'Monitor Progress: Use the "Downloads" tab to track live progress and refresh the page to reset the scanner.',
          ]),
          P(
            "You can view the complete step-by-step tutorial here: https://youtu.be/jDFMOBT8n2k?si=8E7rlp7xI7inA6uD",
          ),
        ],
      },
      {
        question: "How to translate messages using Telegram Web Translator?",
        blocks: [
          P(
            "Telegram Web Translator provides seamless message translation directly within your browser. Follow these steps:",
          ),
          UL([
            "Install the extension: Install the Lang Translator for Telegram from the Chrome Web Store and pin it.",
            "Translate incoming messages: Open Telegram Web, choose your target language in the extension, and click the icon next to any message to translate it.",
            'Identify languages: Use the "Detect Language" tool if you receive a message in an unfamiliar tongue.',
            "Translate replies: Write your message in your native language and click the translator icon to convert it into the recipient's language.",
            "Enable Auto-Translate: Activate the toggle to have your chats translated automatically in real-time.",
          ]),
          P(
            "You can view the complete step-by-step tutorial here: https://youtu.be/Mid0uRvln0s?si=EQDXlFf8s1-cCvtg",
          ),
        ],
      },
      {
        question: "How to use Telegram Web Privacy Extension?",
        blocks: [
          P(
            "An extension that ensures screen privacy by letting you instantly blur messages, media, and contact details on Telegram Web until you hover over them. You can get started using the steps below:",
          ),
          UL([
            'Switch on privacy settings: Click on "Toggle All Privacy Features" in the extension popup to instantly blur all elements.',
            'Customize your view: Select individual options like "Blur Messages" or "Blur Profile Pictures" to choose exactly what stays hidden.',
            "Set your blur strength: Use the intensity slider to adjust the blur effect from soft to strong for your ideal level of privacy.",
          ]),
          P(
            "You can view the complete step-by-step tutorial here: https://www.youtube.com/watch?v=OXeTc3IT_RU",
          ),
        ],
      },
    ],
  },
  {
    id: "guidelines-safety",
    label: "Guidelines/Safety",
    items: [
      {
        question: "Is it safe to use Telegram Translator?",
        blocks: [
          P("Yes, it's generally safe because:"),
          UL([
            "It works locally in your browser and helps translate messages in real time.",
            "It is a third-party tool and not affiliated with Telegram.",
            "Always install it from trusted sources like the official Chrome Web Store.",
          ]),
        ],
      },
      {
        question: "Is it safe to use Telegram Video Downloader?",
        blocks: [
          P("Yes, it's generally safe because:"),
          UL([
            "It runs in your browser and allows you to download media without sending data externally.",
            "It is a third-party tool and not affiliated with Telegram.",
            "Always install it from trusted sources like the official Chrome Web Store.",
          ]),
        ],
      },
      {
        question: "Is it safe to use Telegram Bulk Sender?",
        blocks: [
          P("Yes, it's generally safe because:"),
          UL([
            "It runs in your browser and helps automate messaging.",
            "It is a third-party tool and not affiliated with Telegram.",
            "Always install it from trusted sources like the official Chrome Web Store.",
          ]),
        ],
      },
    ],
  },
  {
    id: "banning",
    label: "Banning",
    items: [
      {
        question:
          "How can I safely send bulk messages using TG Sender without getting banned?",
        blocks: [
          P(
            "Here is how to safely use Ultimate TG Sender without getting banned:",
          ),
          UL([
            "Set a safe delay: Configure a random time gap of 30 to 60 seconds between each message to mimic natural typing speeds and avoid looking like a bot.",
            "Keep daily limits low: Restrict your bulk outreach to 30–50 messages per day per account when contacting people who have no prior chat history with you.",
            "Personalize every message: Use names, variables, and the tool's unique timestamp feature so that your text changes slightly each time, preventing Telegram from flagging identical text as spam.",
            "Warm up your account first: Spend 10–14 days filling out your profile picture, bio, and username, and chat naturally with friends to build up your account's trust score before blasting.",
            "Reach out via usernames: Initiate conversations using Telegram usernames rather than raw phone numbers whenever possible, as Telegram's algorithms are much less restrictive with username lookups.",
          ]),
        ],
      },
    ],
  },
];
